import React from 'react';
import Icon from '../../../components/AppIcon';

const MaturityScoreCard = ({ currentScore, trend, latestScores }) => {
  const getTrendIcon = () => {
    if (trend?.direction === 'up') return 'TrendingUp';
    if (trend?.direction === 'down') return 'TrendingDown';
    return 'Minus';
  };

  const getTrendColor = () => {
    if (trend?.direction === 'up') return 'text-green-400';
    if (trend?.direction === 'down') return 'text-red-400';
    return 'text-gray-400';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-[#1FB5A5]';
    if (score >= 40) return 'text-yellow-400';
    return 'text-orange-400';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Optimized';
    if (score >= 60) return 'Managed';
    if (score >= 40) return 'Defined';
    if (score >= 20) return 'Developing';
    return 'Initial';
  };

  const dimensions = [
    { key: 'data_infrastructure', label: 'Data Infrastructure', icon: 'Database' },
    { key: 'analytics_capabilities', label: 'Analytics Capabilities', icon: 'BarChart3' },
    { key: 'governance_maturity', label: 'Governance Maturity', icon: 'Shield' },
    { key: 'team_skills', label: 'Team Skills', icon: 'Users' },
    { key: 'strategic_alignment', label: 'Strategic Alignment', icon: 'Target' }
  ];

  return (
    <div className="mb-8">
      <div className="bg-white rounded-xl shadow-elevation-2 p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#002B45]">Current Data Maturity Score</h2>
          <div className="flex items-center gap-2">
            <Icon name={getTrendIcon()} size={24} className={getTrendColor()} />
            <span className={`text-lg font-semibold ${getTrendColor()}`}>
              {trend?.percentage}% {trend?.direction === 'up' ? 'improvement' : trend?.direction === 'down' ? 'decline' : 'stable'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Overall Score */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-[#002B45] to-[#003D5C] rounded-xl p-6 text-center">
              <p className="text-gray-300 text-sm mb-2">Overall Maturity</p>
              <div className={`text-6xl font-bold mb-2 ${getScoreColor(currentScore)}`}>
                {currentScore}
              </div>
              <div className="text-[#1FB5A5] text-lg font-semibold mb-4">
                {getScoreLabel(currentScore)}
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-[#1FB5A5] to-green-400 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${currentScore}%` }}
                />
              </div>
            </div>
          </div>

          {/* Dimension Breakdown */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dimensions?.map((dimension) => {
                const dimensionData = latestScores?.[dimension?.key];
                const score = dimensionData?.score || 0;
                const improvement = dimensionData?.improvementPercentage || 0;

                return (
                  <div
                    key={dimension?.key}
                    className="bg-gray-50 rounded-lg p-4 hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-[#1FB5A5]/10 rounded-lg flex items-center justify-center">
                          <Icon name={dimension?.icon} size={20} className="text-[#1FB5A5]" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#002B45]">{dimension?.label}</p>
                          <p className="text-xs text-gray-500">{getScoreLabel(score)}</p>
                        </div>
                      </div>
                      <div className={`text-2xl font-bold ${getScoreColor(score)}`}>
                        {score}
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-[#1FB5A5] h-2 rounded-full transition-all duration-500"
                        style={{ width: `${score}%` }}
                      />
                    </div>
                    {improvement > 0 && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
                        <Icon name="TrendingUp" size={12} />
                        <span>+{improvement?.toFixed(1)}% improvement</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaturityScoreCard;